import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Power } from 'lucide-react';
import { PageBody, PageHeader } from '@/components/AppShell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Dialog, DialogBody, DialogFooter } from '@/components/ui/dialog';
import { SortHeader, makeToggleSort, cmp, type SortState } from '@/components/ui/sort-header';
import { accountsApi, type Account } from '@/api';
import { ApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';

type AccountType = 'CASH' | 'BANK' | 'CREDIT_CARD' | 'OTHER';

const typeOrder: Record<AccountType, number> = {
  CASH: 0,
  BANK: 1,
  CREDIT_CARD: 2,
  OTHER: 3,
};

const typeLabel: Record<AccountType, string> = {
  CASH: 'Caixa',
  BANK: 'Banco',
  CREDIT_CARD: 'Cartão de crédito',
  OTHER: 'Outro',
};

function TypeBadge({ type }: { type: string }) {
  switch (type) {
    case 'CASH':        return <Badge variant="success">Caixa</Badge>;
    case 'BANK':        return <Badge variant="info">Banco</Badge>;
    case 'CREDIT_CARD': return <Badge variant="warning">Cartão</Badge>;
    case 'OTHER':       return <Badge>Outro</Badge>;
    default:            return <Badge>{type}</Badge>;
  }
}

function formatCurrency(value: string | number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value));
}

type AccountSortColumn = 'name' | 'type' | 'bankCode' | 'balance' | 'active';

export function ContasPage() {
  const qc = useQueryClient();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'ADMIN';

  const [sort, setSort] = useState<SortState<AccountSortColumn>>({ column: 'name', dir: 'asc' });
  const toggleSort = makeToggleSort(setSort);

  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);
  const [toggling, setToggling] = useState<Account | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: accountsApi.list,
  });

  const sortedItems = useMemo(() => {
    const rows = data ?? [];
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let primary = 0;
      switch (sort.column) {
        case 'name':     primary = cmp.text(a.name, b.name); break;
        case 'type':     primary = (typeOrder[a.type as AccountType] ?? 99) - (typeOrder[b.type as AccountType] ?? 99); break;
        case 'bankCode': primary = cmp.text(a.bankCode ?? '', b.bankCode ?? ''); break;
        case 'balance':  primary = cmp.number(a.balance, b.balance); break;
        case 'active':   primary = cmp.bool(a.active, b.active); break;
      }
      if (primary !== 0) return primary * factor;
      return cmp.text(a.name, b.name);
    });
  }, [data, sort]);

  function refresh() {
    qc.invalidateQueries({ queryKey: ['accounts'] });
  }

  return (
    <>
      <PageHeader
        title="Contas / Caixas"
        breadcrumb="Cadastros"
        actions={
          isAdmin && (
            <Button size="sm" onClick={() => setOpenNew(true)}>
              <Plus className="w-4 h-4" /> Nova conta
            </Button>
          )
        }
      />
      <PageBody>
        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-muted-foreground">
              <tr>
                <SortHeader column="name"     label="Nome"      sort={sort} onClick={toggleSort} />
                <SortHeader column="type"     label="Tipo"      sort={sort} onClick={toggleSort} />
                <SortHeader column="bankCode" label="Banco/Ag"  sort={sort} onClick={toggleSort} />
                <SortHeader column="balance"  label="Saldo"     sort={sort} onClick={toggleSort} align="right" />
                <SortHeader column="active"   label="Status"    sort={sort} onClick={toggleSort} />
                {isAdmin && <th className="px-3 py-2 font-medium w-20 text-right">Ações</th>}
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading && (
                <tr>
                  <td colSpan={isAdmin ? 6 : 5} className="px-3 py-6 text-center text-muted-foreground">
                    Carregando…
                  </td>
                </tr>
              )}
              {sortedItems.map((a) => (
                <tr key={a.id} className="group">
                  <td className="px-3 py-2 font-medium">{a.name}</td>
                  <td className="px-3 py-2"><TypeBadge type={a.type} /></td>
                  <td className="px-3 py-2 text-muted-foreground text-xs">
                    {a.bankCode || a.agency || a.accountNumber
                      ? [a.bankCode, a.agency, a.accountNumber].filter(Boolean).join(' / ')
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatCurrency(a.balance)}
                  </td>
                  <td className="px-3 py-2">
                    {a.active
                      ? <Badge variant="success">Ativa</Badge>
                      : <Badge variant="danger">Inativa</Badge>}
                  </td>
                  {isAdmin && (
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setEditing(a)}
                          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                          title="Editar"
                          aria-label="Editar conta"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setToggling(a)}
                          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-destructive"
                          title={a.active ? 'Inativar' : 'Reativar'}
                          aria-label={a.active ? 'Inativar conta' : 'Reativar conta'}
                        >
                          <Power className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {!isLoading && (data?.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 6 : 5} className="px-3 py-6 text-center text-muted-foreground">
                    Nenhuma conta cadastrada
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-muted-foreground">
          Contas são os caixas físicos ou bancários usados para registrar entradas e saídas.
          Não é possível excluir uma conta — para ocultá-la dos seletores, inative-a.
        </p>
      </PageBody>

      {openNew && (
        <AccountFormDialog
          editing={null}
          onClose={() => setOpenNew(false)}
          onSaved={() => { setOpenNew(false); refresh(); }}
        />
      )}
      {editing && (
        <AccountFormDialog
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
      {toggling && (
        <ToggleActiveDialog
          target={toggling}
          onClose={() => setToggling(null)}
          onDone={() => { setToggling(null); refresh(); }}
        />
      )}
    </>
  );
}

// ===================== Form (criar / editar) =====================

function AccountFormDialog({
  editing,
  onClose,
  onSaved,
}: {
  editing: Account | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!editing;
  const [name, setName] = useState(editing?.name ?? '');
  const [type, setType] = useState<AccountType>((editing?.type as AccountType) ?? 'BANK');
  const [bankCode, setBankCode] = useState(editing?.bankCode ?? '');
  const [agency, setAgency] = useState(editing?.agency ?? '');
  const [accountNumber, setAccountNumber] = useState(editing?.accountNumber ?? '');
  const [initialBalance, setInitialBalance] = useState(
    editing ? String(editing.initialBalance ?? '0') : '0',
  );
  const [active, setActive] = useState(editing?.active ?? true);

  const create = useMutation({
    mutationFn: () =>
      accountsApi.create({
        name,
        type,
        bankCode: bankCode || undefined,
        agency: agency || undefined,
        accountNumber: accountNumber || undefined,
        initialBalance: Number(initialBalance) || 0,
      } as Partial<Account> & { initialBalance: number }),
    onSuccess: onSaved,
  });

  const update = useMutation({
    mutationFn: () =>
      accountsApi.update(editing!.id, {
        name,
        type,
        bankCode: bankCode || undefined,
        agency: agency || undefined,
        accountNumber: accountNumber || undefined,
        active,
      }),
    onSuccess: onSaved,
  });

  const mut = isEdit ? update : create;
  const apiError = mut.error instanceof ApiError ? mut.error.message : null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    mut.mutate();
  }

  return (
    <Dialog open onClose={onClose} title={isEdit ? `Editar conta — ${editing!.name}` : 'Nova conta'}>
      <form onSubmit={handleSubmit}>
        <DialogBody className="space-y-3">
          <div className="space-y-1">
            <Label>Nome</Label>
            <Input
              autoFocus
              maxLength={120}
              placeholder="Ex.: Renan, Renato, Itaú PJ…"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-1">
            <Label>Tipo</Label>
            <Select value={type} onChange={(e) => setType(e.target.value as AccountType)}>
              <option value="CASH">{typeLabel.CASH}</option>
              <option value="BANK">{typeLabel.BANK}</option>
              <option value="CREDIT_CARD">{typeLabel.CREDIT_CARD}</option>
              <option value="OTHER">{typeLabel.OTHER}</option>
            </Select>
          </div>

          {(type === 'BANK' || type === 'CREDIT_CARD') && (
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Banco (cód.)</Label>
                <Input maxLength={10} value={bankCode ?? ''} onChange={(e) => setBankCode(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Agência</Label>
                <Input maxLength={20} value={agency ?? ''} onChange={(e) => setAgency(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Conta</Label>
                <Input maxLength={30} value={accountNumber ?? ''} onChange={(e) => setAccountNumber(e.target.value)} />
              </div>
            </div>
          )}

          {!isEdit && (
            <div className="space-y-1">
              <Label>Saldo inicial</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={initialBalance}
                onChange={(e) => setInitialBalance(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Valor de partida do saldo. Não pode ser alterado depois — registre lançamentos para ajustar.
              </p>
            </div>
          )}

          {isEdit && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="rounded border-input"
              />
              Ativa
            </label>
          )}

          {apiError && (
            <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2">
              {apiError}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={!name.trim() || mut.isPending}>
            {mut.isPending ? 'Salvando…' : isEdit ? 'Salvar' : 'Criar'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

// ===================== Toggle ativa/inativa =====================

function ToggleActiveDialog({
  target,
  onClose,
  onDone,
}: {
  target: Account;
  onClose: () => void;
  onDone: () => void;
}) {
  const willActivate = !target.active;
  const mut = useMutation({
    mutationFn: () => accountsApi.update(target.id, { active: willActivate }),
    onSuccess: onDone,
  });
  const apiError = mut.error instanceof ApiError ? mut.error.message : null;

  return (
    <Dialog open onClose={onClose} title={willActivate ? 'Reativar conta' : 'Inativar conta'}>
      <DialogBody className="space-y-3">
        <p>
          {willActivate ? 'Reativar' : 'Inativar'} a conta <strong>{target.name}</strong>?
        </p>
        <p className="text-sm text-muted-foreground">
          {willActivate
            ? 'A conta voltará a aparecer nos seletores de lançamentos.'
            : 'A conta deixará de aparecer nos seletores de novos lançamentos. Os lançamentos existentes permanecem inalterados.'}
        </p>
        {apiError && (
          <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2">
            {apiError}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
        <Button
          type="button"
          variant={willActivate ? 'default' : 'destructive'}
          disabled={mut.isPending}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? 'Salvando…' : willActivate ? 'Reativar' : 'Inativar'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
